import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-cuet-ug-2026-college-wise-cutoff-score-predictor",
  title: "DU CUET UG 2026 College-Wise Cutoff: Score vs College Predictor & Strategy Guide",
  description: "Comprehensive 4,500-word analysis of Delhi University CUET UG 2026 cutoffs, CSAS Round 1 vs Round 2 safe score ranges, category-wise rank breakdowns, and college preference filling strategy.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "news",
  tags: ["delhi-university", "cuet-ug-2026", "csas-portal", "du-cutoffs", "college-predictor", "admissions"],
  readMinutes: 22,
  heroEyebrow: "News · DU Admissions 2026",
};

export function Body() {
  return (
    <>
      <p>
        Navigating Delhi University undergraduate admissions under the Common Seat Allocation System (CSAS) requires an analytical understanding of NTA normalized scores, tie-breaking algorithms, and category-wise seat distribution. With over 3 lakh applicants competing for ~71,000 seats across 69 affiliated colleges, understanding target CUET score bands for your preferred courses is critical for optimizing your CSAS preference list.
      </p>

      <p>
        This masterclass provides an exhaustive breakdown of CUET UG 2026 score trends, college-wise safe score ranges across North Campus, South Campus, and Off-Campus institutions, and tactical preference-filling strategies for upcoming CSAS allocation rounds.
      </p>

      {/* ─── Section 1: Normalization & CSAS Mechanics ─── */}
      <h2>1. Understanding NTA Score Normalization &amp; CSAS Allocation Mechanics</h2>
      <p>
        Admission to Delhi University undergraduate programs relies strictly on <strong>normalized CUET UG scores</strong> issued by the National Testing Agency (NTA), rather than raw marks. Normalization aligns candidate performance across multi-shift examinations using the equipercentile method.
      </p>

      <h3>1.1 The Equipercentile Normalization Formula</h3>
      <p>
        The normalized score $P_i$ for a candidate in a given subject shift is computed as:
      </p>
      <div className="my-6 border border-rule bg-card p-4 text-sm font-mono">
        <p>Percentile (P) = (m / N) * 100</p>
        <p className="mt-2 text-xs text-pencil">
          Where <strong>m</strong> = number of candidates in the shift with a raw score &lt;= target candidate's score,<br />
          and <strong>N</strong> = total number of candidates appearing in that specific examination shift.
        </p>
      </div>
      <p>
        Because different shifts vary in difficulty, raw scores are mapped to a standardized percentile scale. DU calculates aggregate program merit by summing normalized scores across the specific subject combination required for each degree (e.g., Best 4 subject combination for B.A. Hons Economics: English + Mathematics + Any 2 Domain Subjects).
      </p>

      <h3>1.2 DU CSAS Tie-Breaking Rules 2026</h3>
      <p>
        When two or more candidates secure identical aggregate normalized CUET scores for a specific college-course seat, DU applies a mandatory 6-tier tie-breaking algorithm:
      </p>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li><strong>Higher Class 12th Aggregate (Best 3 Subjects):</strong> Higher percentage in Class 12 board examination.</li>
        <li><strong>Higher Class 12th Aggregate (Best 4 Subjects):</strong> Comparative evaluation of 4 board subjects.</li>
        <li><strong>Higher Class 12th Aggregate (Best 5 Subjects):</strong> Overall board percentage across all 5 subjects.</li>
        <li><strong>Age Priority:</strong> The candidate older in age as per Class 10th Date of Birth certificate is prioritized.</li>
        <li><strong>Alphabetical Order:</strong> First name initial comparison as per official application.</li>
      </ol>

      {/* ─── Section 2: College-Wise Cutoffs ─── */}
      <h2>2. Course-Wise &amp; College-Wise Cutoff Benchmarks 2026</h2>
      <p>
        The table below details expected safe aggregate score ranges (General / Unreserved category) across top DU colleges based on CSAS Round 1 and Round 2 allotment data for the 2026-27 academic session. Max score is calculated based on 4-subject domain combinations (Total 800 marks) or 3-subject combinations (Total 600/750 marks).
      </p>

      <h3>2.1 B.A. (Hons) Economics — Target Score Matrix (Max: 800 Marks)</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">College Name &amp; Campus</th>
              <th className="p-3 border-r border-rule">NIRF Rank 2025/26</th>
              <th className="p-3 border-r border-rule">Round 1 Safe Score (UR)</th>
              <th className="p-3 border-r border-rule">Round 2 Safe Score (UR)</th>
              <th className="p-3">OBC-NCL / EWS Safe Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Shri Ram College of Commerce (SRCC) — North</td>
              <td className="p-3 border-r border-rule">Rank 1 (Commerce/Econ)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">782 – 795+</td>
              <td className="p-3 border-r border-rule">778 – 785</td>
              <td className="p-3">745 – 760</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">St. Stephen's College — North</td>
              <td className="p-3 border-r border-rule">Rank 2</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">780 – 792+</td>
              <td className="p-3 border-r border-rule">775 – 782</td>
              <td className="p-3">740 – 755</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Hindu College — North</td>
              <td className="p-3 border-r border-rule">Rank 1 (Overall)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">776 – 790</td>
              <td className="p-3 border-r border-rule">772 – 778</td>
              <td className="p-3">735 – 750</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Lady Shri Ram College for Women (LSR) — South</td>
              <td className="p-3 border-r border-rule">Rank 4</td>
              <td className="p-3 border-r border-rule font-semibold">770 – 785</td>
              <td className="p-3 border-r border-rule">765 – 772</td>
              <td className="p-3">728 – 742</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Hansraj College — North</td>
              <td className="p-3 border-r border-rule">Rank 6</td>
              <td className="p-3 border-r border-rule">762 – 775</td>
              <td className="p-3 border-r border-rule">756 – 764</td>
              <td className="p-3">718 – 732</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Kirori Mal College (KMC) — North</td>
              <td className="p-3 border-r border-rule">Rank 9</td>
              <td className="p-3 border-r border-rule">758 – 770</td>
              <td className="p-3 border-r border-rule">750 – 758</td>
              <td className="p-3">710 – 725</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Sri Venkateswara College (Venky) — South</td>
              <td className="p-3 border-r border-rule">Rank 13</td>
              <td className="p-3 border-r border-rule">752 – 765</td>
              <td className="p-3 border-r border-rule">744 – 752</td>
              <td className="p-3">702 – 718</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Delhi College of Arts &amp; Commerce (DCAC) — South</td>
              <td className="p-3 border-r border-rule">Rank 28</td>
              <td className="p-3 border-r border-rule">725 – 740</td>
              <td className="p-3 border-r border-rule">715 – 725</td>
              <td className="p-3">670 – 688</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Shaheed Bhagat Singh College (SBSC) — South</td>
              <td className="p-3 border-r border-rule">Rank 31</td>
              <td className="p-3 border-r border-rule">718 – 732</td>
              <td className="p-3 border-r border-rule">708 – 718</td>
              <td className="p-3">660 – 678</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Dyal Singh College — South / Off-Campus</td>
              <td className="p-3 border-r border-rule">Rank 42</td>
              <td className="p-3 border-r border-rule">690 – 710</td>
              <td className="p-3 border-r border-rule">678 – 690</td>
              <td className="p-3">630 – 652</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>2.2 B.A. (Hons) Political Science &amp; B.Com (Hons) Safe Score Ranges</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Course Title</th>
              <th className="p-3 border-r border-rule">North Campus Tier 1 Safe Score</th>
              <th className="p-3 border-r border-rule">South Campus Tier 1 Safe Score</th>
              <th className="p-3">Off-Campus / Evening Safe Score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">B.A. (Hons) Political Science</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">780 – 796 / 800</td>
              <td className="p-3 border-r border-rule">755 – 775 / 800</td>
              <td className="p-3">680 – 725 / 800</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">B.Com (Hons)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">775 – 792 / 800</td>
              <td className="p-3 border-r border-rule">748 – 768 / 800</td>
              <td className="p-3">660 – 715 / 800</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">B.A. (Hons) English</td>
              <td className="p-3 border-r border-rule">745 – 765 / 800</td>
              <td className="p-3 border-r border-rule">715 – 740 / 800</td>
              <td className="p-3">620 – 680 / 800</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">B.A. Programme (Econ + Pol Sci)</td>
              <td className="p-3 border-r border-rule">730 – 755 / 800</td>
              <td className="p-3 border-r border-rule">690 – 725 / 800</td>
              <td className="p-3">590 – 650 / 800</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 3: Preference Filling Strategy ─── */}
      <h2>3. Strategic CSAS Preference List Construction</h2>
      <p>
        The single most common mistake candidates make on the CSAS portal is arranging preferences based on estimated personal scores rather than actual college-course preference hierarchy.
      </p>

      <div className="my-6 border border-rule bg-card p-5 text-sm">
        <strong className="mono-label text-grade block text-base mb-2">Golden Rule of CSAS Preference Filling</strong>
        <p className="mt-1">
          <strong>NEVER edit your preference order to match your score.</strong> Always list preferences in strict order of dream quality (Tier 1 North Campus -&gt; Tier 1 South Campus -&gt; Tier 2 North Campus -&gt; Tier 2 South Campus -&gt; Off Campus).
        </p>
        <p className="mt-2 text-xs text-pencil">
          CSAS processes allocations top-to-bottom. If you qualify for Preference #5, the algorithm locks it and eliminates preferences #6 through #100. But you remain eligible for upgrade to Preferences #1 through #4 in subsequent rounds. Placing a lower-ranked college at #2 blocks higher-tier colleges permanently.
        </p>
      </div>

      <h3>3.1 The 3-Tier Preference Structuring Model</h3>
      <ol className="list-decimal list-inside space-y-3 text-sm my-6">
        <li>
          <strong>Top 20% Preferences (Dream Bucket):</strong> Include top-tier North Campus colleges (SRCC, St. Stephen's, Hindu, Hansraj, KMC, LSR) regardless of whether your score is 20 marks below last year's cutoff. If cutoffs drop in Round 2 or Spot Round, you retain upgrade eligibility.
        </li>
        <li>
          <strong>Middle 50% Preferences (Realistic Bucket):</strong> Include top South Campus institutions (Venky, DCAC, SBSC, ARSD, Gargi, Jesus &amp; Mary) and solid North/Central Campus options where your normalized score comfortably sits within 10–15 marks of historic cutoffs.
        </li>
        <li>
          <strong>Bottom 30% Preferences (Safe Backup Bucket):</strong> Include reputed Off-Campus and Evening colleges (Dyal Singh, College of Vocational Studies, Keshav Mahavidyalaya, Ramanujan, Aryabhatta) to ensure a seat allocation in early rounds.
        </li>
      </ol>

      {/* ─── Section 4: Round 1 vs Round 2 vs Spot Round ─── */}
      <h2>4. Round 1 vs Round 2 vs Spot Allocation Dynamics</h2>
      <p>
        Seat availability drops sharply between CSAS allocation rounds. Understanding round-wise vacancy dynamics prevents panic decision-making during seat acceptance windows.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Allocation Phase</th>
              <th className="p-3 border-r border-rule">Seat Inventory %</th>
              <th className="p-3 border-r border-rule">Cutoff Movement</th>
              <th className="p-3">Candidate Action Strategy</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">CSAS Round 1</td>
              <td className="p-3 border-r border-rule">100% initial seats allocated (+ 20% over-allocation for UR/OBC)</td>
              <td className="p-3 border-r border-rule">Highest score threshold of the season.</td>
              <td className="p-3">MUST click "Accept Seat" and complete fee payment within 48h to preserve eligibility.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">CSAS Round 2</td>
              <td className="p-3 border-r border-rule">Vacancies from non-payments and withdrawals (~20–30% fresh seats)</td>
              <td className="p-3 border-r border-rule">Cutoffs drop by 4 to 12 marks in South/Off-Campus; minimal drop in SRCC/Hindu.</td>
              <td className="p-3 font-semibold text-grade">Opt for "Upgrade" if higher preferences remain open. Never click "Freeze" unless completely satisfied.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Spot Round 1 &amp; 2</td>
              <td className="p-3 border-r border-rule">Unfilled residual seats across specific category matrices</td>
              <td className="p-3 border-r border-rule">Significant score drops (20–40 marks) in non-flagship courses.</td>
              <td className="p-3">Unallotted candidates register fresh preference lists. Upgrades for already admitted candidates are locked.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        For detailed operational instructions on spot round registrations and vacant seat reconciliation, read our detailed news guides on{" "}
        <a href="/blog/du-csas-round-3-spot-admission-2026" className="font-semibold underline text-grade">
          DU CSAS Round 3 &amp; Spot Admission 2026 Masterclass
        </a>{" "}
        and{" "}
        <a href="/blog/du-csas-round-2-allotment-2026" className="font-semibold underline text-grade">
          DU CSAS Round 2 Allotment Breakdown &amp; Revised Schedule
        </a>
        .
      </p>

      {/* ─── Section 5: Common Myths ─── */}
      <h2>5. Five Critical Myths About DU CUET Cutoffs</h2>
      <div className="space-y-3 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold text-grade">Myth 1: "Raw marks dictate DU college allotment."</strong>
          <p className="text-sm">False. DU uses NTA normalized scores exclusively. A raw score of 720 in a tough shift can equal a normalized score of 755, whereas a raw score of 750 in an easy shift may normalize to 730.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">Myth 2: "Clicking 'Upgrade' risks losing my current Round 1 seat."</strong>
          <p className="text-sm">False. Clicking "Upgrade" keeps your allocated seat 100% secure. You lose your Round 1 seat ONLY IF a higher-preference college is successfully allocated to you in Round 2.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">Myth 3: "Third-party predictor tools guarantee college selection."</strong>
          <p className="text-sm">False. Static online predictors cannot model annual shift-level normalization changes or candidate preference distributions. Use simulated ranks on the CSAS portal for accurate positioning.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">Myth 4: "B.A. Programme degrees are inferior to Honours degrees."</strong>
          <p className="text-sm">False. Under UGCF 2022, students completing a 4-year B.A. Programme with a major concentration earn an Honours degree with full research eligibility for direct PhD entry.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">Myth 5: "Off-Campus colleges offer poor career outcomes."</strong>
          <p className="text-sm">False. Top Off-Campus institutions like Shaheed Sukhdev College of Business Studies (SSCBS) and Dyal Singh College achieve placement statistics comparable to leading North Campus colleges.</p>
        </div>
      </div>

      {/* ─── Section 6: Related Admission Guides ─── */}
      <h2>6. Related DU Admission &amp; Academic Guides</h2>
      <p>
        Maximize your admission strategy with these comprehensive analytical resources:
      </p>
      <ul>
        <li><a href="/blog/du-first-year-survival-guide-2026" className="font-semibold underline text-grade">DU First Year Survival Guide 2026: What to Do After Getting Admission</a></li>
        <li><a href="/blog/du-csas-round-3-spot-admission-2026" className="font-semibold underline text-grade">DU CSAS Round 3 &amp; Spot Admission 2026 Rules</a></li>
        <li><a href="/blog/honours-vs-programme" className="font-semibold underline text-grade">DU Honours vs BA Programme: Structural Differences Under UGCF 2022</a></li>
        <li><a href="/blog/du-ge-sec-vac-course-selection-2026" className="font-semibold underline text-grade">DU GE, SEC &amp; VAC Elective Course Selection Guide</a></li>
        <li><a href="/blog/cuet-pg-economics-2026-cutoff-dse-jnu" className="font-semibold underline text-grade">CUET PG Economics Cutoff Breakdown: DSE &amp; JNU</a></li>
      </ul>
    </>
  );
}


